import type { Condition, ConditionalConfig } from '@viapeople/components/src/components/forms/conditionalTypes'
import { useMemo } from 'react'
import { type Control, useWatch } from 'react-hook-form'
import type { ProcessedFormComponent } from './types'

export function evaluateCondition(
	condition: Condition,
	formValues: Record<string, string | number | boolean | null | undefined>,
): boolean {
	const sourceValue = formValues[condition.sourceComponentId]
	switch (condition.operator) {
		case 'equals':
			return condition.values?.some((v) => String(sourceValue) === String(v)) ?? false
		case 'not_equals':
			if (sourceValue === null || sourceValue === undefined || sourceValue === '') return true
			return !(condition.values?.some((v) => String(sourceValue) === String(v)) ?? false)
		case 'has_value':
			return sourceValue !== null && sourceValue !== undefined && sourceValue !== ''
		case 'is_empty':
			return sourceValue === null || sourceValue === undefined || sourceValue === ''
		case 'is_checked':
			return sourceValue === true || sourceValue === 'true'
		case 'is_not_checked':
			return sourceValue === false || sourceValue === 'false' || sourceValue === null || sourceValue === undefined
		case 'greater_than': {
			const num = Number(sourceValue)
			const threshold = Number(condition.values?.[0])
			return !Number.isNaN(num) && !Number.isNaN(threshold) && num > threshold
		}
		case 'less_than': {
			const num = Number(sourceValue)
			const threshold = Number(condition.values?.[0])
			return !Number.isNaN(num) && !Number.isNaN(threshold) && num < threshold
		}
		default:
			return false
	}
}

export function evaluateConditionalConfig(
	config: ConditionalConfig,
	formValues: Record<string, string | number | boolean | null | undefined>,
): boolean {
	if (config.conditions.length === 0) return true
	if (config.compoundOperator === 'and') {
		return config.conditions.every((c) => evaluateCondition(c, formValues))
	}
	return config.conditions.some((c) => evaluateCondition(c, formValues))
}

export function collectComponentIds(component: ProcessedFormComponent): string[] {
	const ids = [component.id]
	if (component.children) {
		for (const child of component.children) {
			ids.push(...collectComponentIds(child))
		}
	}
	return ids
}

export function useConditionalVisibility(
	formComponents: ProcessedFormComponent[],
	control: Control<Record<string, string | number | boolean>>,
	savedSourceValues: Record<string, string> = {},
): {
	hiddenComponentIds: Set<string>
	isHidden: (componentId: string) => boolean
} {
	const conditionalEntries = useMemo(() => {
		const entries: Array<{
			id: string
			config: ConditionalConfig
			component: ProcessedFormComponent
		}> = []
		const walk = (components: ProcessedFormComponent[]) => {
			for (const comp of components) {
				const config = (comp.custom as { conditional?: ConditionalConfig }).conditional
				if (config && config.conditions.length > 0) {
					entries.push({ id: comp.id, config, component: comp })
				}
				if (comp.children) walk(comp.children)
			}
		}
		walk(formComponents)
		return entries
	}, [formComponents])

	// Build a baseline of component values from the server data (component.value).
	// This ensures conditionals evaluate correctly on initial render before
	// Controllers mount, and prevents the chicken-and-egg problem where a hidden
	// component never mounts so its value never registers in the form.
	const componentValues = useMemo(() => {
		const values: Record<string, string | number | boolean | null | undefined> = {}
		const walk = (components: ProcessedFormComponent[]) => {
			for (const comp of components) {
				if (comp.value !== undefined && comp.value !== null && comp.value !== '') {
					values[comp.id] = comp.value as string | number | boolean
				}
				if (comp.children) walk(comp.children)
			}
		}
		walk(formComponents)
		return values
	}, [formComponents])

	// Collect ALL source component IDs referenced in any conditional
	const sourceIds = useMemo(() => {
		const ids = new Set<string>()
		for (const entry of conditionalEntries) {
			for (const condition of entry.config.conditions) {
				ids.add(condition.sourceComponentId)
			}
		}
		return Array.from(ids)
	}, [conditionalEntries])

	// Watch source fields by name so useWatch returns a new array reference on
	// each change — useWatch({ control }) without name returns the same internal
	// object reference, which prevents useMemo from detecting changes.
	const watchedValues = useWatch({ control, name: sourceIds })

	const hiddenComponentIds = useMemo(() => {
		const hidden = new Set<string>()
		// Layer values with increasing priority:
		// 1. Saved values from DB (cross-user fallback for absent/read-only sources)
		// 2. Server-side component values (per-user authoritative baseline)
		// 3. Live form values from useWatch (overrides once Controllers mount)
		const formValues: Record<string, string | number | boolean | null | undefined> = {
			...savedSourceValues,
			...componentValues,
		}
		for (let i = 0; i < sourceIds.length; i++) {
			const val = watchedValues[i]
			if (val !== undefined) {
				formValues[sourceIds[i]] = val
			}
		}

		for (const entry of conditionalEntries) {
			const isVisible = evaluateConditionalConfig(entry.config, formValues)
			if (!isVisible) {
				for (const id of collectComponentIds(entry.component)) {
					hidden.add(id)
				}
			}
		}
		return hidden
	}, [conditionalEntries, componentValues, sourceIds, watchedValues, savedSourceValues])

	const isHidden = useMemo(() => (componentId: string) => hiddenComponentIds.has(componentId), [hiddenComponentIds])

	return { hiddenComponentIds, isHidden }
}
